import { X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils';

import { Button } from './button';

const TAMANOS = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
} as const;

export type DialogSize = keyof typeof TAMANOS;

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  /** Acciones al pie del diálogo (normalmente botones). */
  footer?: ReactNode;
  size?: DialogSize;
}

/**
 * Diálogo modal (portal a `document.body`).
 *
 * Cierra con `Escape` o al pulsar el fondo, bloquea el scroll de la página y
 * mueve el foco al panel al abrirse. Se usa en las pantallas que necesitan
 * capturar datos sin salir de la lista (códigos de calibración, tomas RIC…).
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: DialogProps) {
  const idTitulo = useId();
  const idDescripcion = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    const alPresionar = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', alPresionar);
    panelRef.current?.focus();

    return () => {
      document.body.style.overflow = overflowPrevio;
      document.removeEventListener('keydown', alPresionar);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    // Centrado con `my-auto` en el panel (y no con `items-center`): así el
    // diálogo queda en el centro de la pantalla, pero si su contenido es más
    // alto que la ventana, el fondo sigue siendo desplazable y el inicio del
    // diálogo nunca queda cortado.
    //
    // En móvil se comporta como una hoja a pantalla completa (sin márgenes ni
    // esquinas redondeadas) y el que se desplaza es el cuerpo del diálogo, para
    // que el título y el botón de cerrar sigan siempre a la vista.
    <div className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-ink-950/45 px-0 py-0 backdrop-blur-sm sm:px-4 sm:py-8">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        aria-describedby={description ? idDescripcion : undefined}
        tabIndex={-1}
        className={cn(
          'relative my-auto flex max-h-dvh w-full flex-col overflow-hidden bg-white shadow-xl focus:outline-none sm:max-h-[calc(100dvh-4rem)] sm:rounded-card sm:border sm:border-ink-100',
          TAMANOS[size],
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-ink-100 px-4 py-3 sm:px-5 sm:py-4">
          <div className="min-w-0">
            <h2 id={idTitulo} className="text-base font-bold tracking-tight text-ink-900">
              {title}
            </h2>
            {description ? (
              <p id={idDescripcion} className="mt-0.5 text-xs text-ink-500">
                {description}
              </p>
            ) : null}
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="-mr-1 flex size-8 shrink-0 items-center justify-center rounded-full text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-700"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">{children}</div>

        {footer ? (
          <footer className="flex flex-wrap items-center justify-end gap-3 border-t border-ink-100 px-4 py-3 sm:px-5 sm:py-4">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'primary';
  loading?: boolean;
  /** Campo opcional (ej. el motivo de una anulación). */
  children?: ReactNode;
  onConfirm: () => void;
  onClose: () => void;
}

/** Confirmación modal reutilizable (sustituye a `window.confirm`). */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  tone = 'danger',
  loading = false,
  children,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="outline" size="sm" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            size="sm"
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children ?? (
        <p className="text-sm leading-relaxed text-ink-600">
          Esta acción no se puede deshacer. Revisa la información antes de continuar.
        </p>
      )}
    </Dialog>
  );
}
